import React from "react";

/**
 * The single enforcement point for the spec's absence-vs-zero rule:
 *
 *   "A scheduled broadcast has no counters yet. Render `—`, never `0` —
 *    zero is a result, absence is not."
 *
 * Every counter, rate and metric in Broadcasts goes through `Num` or `Pct`,
 * so a screen cannot accidentally print `0 delivered` for a send that has not
 * started. There is no second code path that formats a number.
 */

/** U+2014. Not a hyphen, not an en dash — the spec's glyph. */
const EM_DASH = "—";

/** Monospace + tabular figures so numeric columns line up across rows. */
const BASE = "font-mono tabular-nums";

/** The em dash is faintest of all the text tiers: it is an absence. */
const DASH_CLASS = "text-bc-faintest";

const EMPHASIS_CLASS = "text-bc-text font-medium";
const NORMAL_CLASS = "text-bc-text-2";

/**
 * Is this a value we can render as a number? `0` is; `null`, `undefined`,
 * `NaN` and `Infinity` are not.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
const hasValue = (value) => typeof value === "number" && Number.isFinite(value);

/**
 * The em dash placeholder. `aria-label` because a screen reader announcing a
 * bare em dash says nothing useful; the visible glyph is kept for sighted
 * readers.
 *
 * @param {Object} props
 * @param {string} [props.className]
 */
const Dash = ({ className = "" }) => (
  <span aria-label="no data" className={`${BASE} ${DASH_CLASS} ${className}`}>
    {EM_DASH}
  </span>
);

/**
 * A single number in the Broadcasts console.
 *
 * Deliberately carries no `title` attribute — a tooltip on every cell of a
 * metrics grid is noise, not affordance.
 *
 * @param {Object} props
 * @param {number|null|undefined} props.value - The count. `null`, `undefined` and `NaN` render `—`; `0` renders `0`.
 * @param {string} [props.className] - Extra classes, merged last so callers can override.
 * @param {boolean} [props.emphasis=false] - Darker, medium weight. Used for the one exact engagement number in a row (Clicked).
 * @returns {React.ReactElement}
 */
export const Num = ({ value, className = "", emphasis = false }) => {
  if (!hasValue(value)) return <Dash className={className} />;

  const toneClass = emphasis ? EMPHASIS_CLASS : NORMAL_CLASS;

  return (
    <span className={`${BASE} ${toneClass} ${className}`}>
      {value.toLocaleString()}
    </span>
  );
};

/**
 * Format a percentage to one decimal place, dropping a trailing `.0`
 * so the console shows `96.3%` and `100%`, never `100.0%`.
 *
 * @param {number} pct
 * @returns {string}
 */
const formatPct = (pct) => `${Math.round(pct * 10) / 10}%`;

/**
 * A rate. The absence rule in its second form: a rate over an empty
 * denominator is undefined, not zero, so `of === 0` renders `—` rather than
 * `0%` or `NaN%`.
 *
 * @param {Object} props
 * @param {number|null|undefined} props.value - Numerator, or an already-computed percentage when `of` is omitted.
 * @param {number} [props.of] - Denominator. Omit when `value` is already a percentage.
 * @param {string} [props.className] - Extra classes, merged last.
 * @returns {React.ReactElement}
 */
export const Pct = ({ value, of, className = "" }) => {
  if (!hasValue(value)) return <Dash className={className} />;

  const isRatio = of !== undefined && of !== null;
  if (isRatio && !(hasValue(of) && of > 0)) return <Dash className={className} />;

  const pct = isRatio ? (value / of) * 100 : value;
  if (!Number.isFinite(pct)) return <Dash className={className} />;

  return (
    <span className={`${BASE} ${NORMAL_CLASS} ${className}`}>
      {formatPct(pct)}
    </span>
  );
};
